/**
 * Riding: mounting the tamed horse and where it stands.
 *
 * The server owns the one durable fact — which horse you tamed (horse.service.ts).
 * Everything here is movement and presentation: the horse waits and grazes where
 * you left it, and you climb on — walking back to it is deliberate, there is no
 * recall — and the gait becomes `mount` at its type's speed.
 *
 * The mount is a state on the player, not a separate sprite riding animation — the
 * hero simply moves faster and the tamed-horse sprite hides while ridden and
 * reappears where you dismount. The pack has horse-riding rows, but composing the
 * hero onto the horse is a bigger art job (Block 1 sprite note); this reads
 * correctly and is the mechanic the brief asks for.
 *
 * Sprites: the tamed horse reuses the animal pack's horse frames, and it uses the
 * SAME convention as fauna.ts — idle/walk × side/down/up, side art facing LEFT, so
 * the sprite is flipped when moving RIGHT. Frames are loaded through Assets here
 * rather than pulled with `Texture.from(url)`: in Pixi v8 `Texture.from(string)` is
 * a bare cache lookup and returns undefined for anything the loader has not already
 * fetched — which is exactly the case when the scene is built for a player who
 * already owns a horse (the fauna loader resolves later, or never).
 */
import { Container, Sprite, Texture } from 'pixi.js';
import { TILE } from './world';

export type HorseType = 'work' | 'racing' | 'heavy';

export type HorseAnim = 'idle' | 'walk';
export type HorseFacing = 'side' | 'down' | 'up';

/** `${anim}_${facing}` → frames. */
export type HorseFrames = Record<string, Texture[]>;
export type HorseFrameLoader = (type: HorseType) => Promise<HorseFrames>;

const ANIM_FRAMES: Record<HorseAnim, number> = { idle: 2, walk: 6 };
const FACINGS: HorseFacing[] = ['side', 'down', 'up'];

/** Load every idle/walk frame of one horse type through Assets (cache-safe). */
export const loadHorseFrames: HorseFrameLoader = async (type) => {
  const { Assets } = await import('pixi.js');
  const urls: Record<string, string> = {};
  for (const anim of Object.keys(ANIM_FRAMES) as HorseAnim[]) {
    for (const facing of FACINGS) {
      for (let f = 0; f < ANIM_FRAMES[anim]; f++) {
        urls[`${anim}_${facing}_${f}`] = `/sprites/animals/horse_${type}_${anim}_${facing}_${f}.png`;
      }
    }
  }
  const loaded = (await Assets.load(Object.values(urls))) as Record<string, Texture>;
  const out: HorseFrames = {};
  for (const anim of Object.keys(ANIM_FRAMES) as HorseAnim[]) {
    for (const facing of FACINGS) {
      const seq: Texture[] = [];
      for (let f = 0; f < ANIM_FRAMES[anim]; f++) {
        const t = loaded[urls[`${anim}_${facing}_${f}`]];
        if (t) seq.push(t);
      }
      if (seq.length) out[`${anim}_${facing}`] = seq;
    }
  }
  return out;
};

export interface HorseTypeInfo {
  id: HorseType;
  speed: number;      // ridden tiles/sec
  fearless: boolean;  // heavy: unspooked by night mobs
}

export interface Riding {
  /** True while the hero is on the horse. */
  mounted: boolean;
  /** The tamed horse's type, or null if none. */
  type: HorseType | null;
  /** Place the tamed horse into the world (after taming or on load). */
  setHorse(type: HorseType, info: HorseTypeInfo, tx: number, ty: number): void;
  removeHorse(): void;
  /** Toggle mount/dismount if the hero is close enough to the horse. Returns the new state. */
  toggle(heroX: number, heroY: number): 'mounted' | 'dismounted' | 'too-far' | 'no-horse';
  /** Per frame: the loose horse grazes around where it was left. It does NOT
   *  follow the hero — you walk back to it and mount (there is no recall). */
  update(dt: number, heroX: number, heroY: number, blocked?: (x: number, y: number) => boolean): void;
  /** Ridden speed of the current horse (tiles/sec), or 0. */
  mountSpeed(): number;
  /** True while riding a fearless (heavy) horse — night mobs won't aggro. */
  fearless(): boolean;
  /** Where the horse currently stands (to persist / re-place). */
  position(): { tx: number; ty: number } | null;
  /** Where the horse's FEET are, in world px (for hit-testing a click on it). */
  pixel(): { x: number; y: number } | null;
  destroy(): void;
}

// The horse is drawn ~1.6 tiles tall, so "standing next to it" spans more ground
// than a 1-tile reach: 2.5 tiles is the distance at which the hero visually
// overlaps the animal. A tighter reach was the difference between pressing E and
// being told "too far" while standing shoulder-to-shoulder with the horse.
const MOUNT_REACH_TILES = 2.5;    // stand this close and press E to mount
const IDLE_FPS = 4;
// Sized to match the horse you actually ride, so mounting/dismounting doesn't make
// the animal jump in size. That match is exact rather than eyeballed: the loose art
// and the mount composited into the hero frame share the same 31×29 native-pixel
// bbox, so it is enough to draw both at the same px-per-native-pixel. The hero frame
// draws at CHAR_SCALE 2.4, and PX is 1.5 here → 1.5 × 1.6 = 2.4.
const HORSE_SCALE = 1.6;
/** Px per native sprite pixel for the loose horse — must equal the hero's CHAR_SCALE. */
export const HORSE_PX_SCALE = (TILE / 32) * HORSE_SCALE;
const GRAZE_SPEED = 1.2;          // slow amble while grazing near home (does NOT chase the hero)
const GRAZE_RADIUS = 2.5;         // wanders within this many tiles of where it was left
const GRAZE_PAUSE_MIN = 3500, GRAZE_PAUSE_MAX = 9000;
const rand = (a: number, b: number) => a + Math.random() * (b - a);

export function createRiding(host?: Container, loader: HorseFrameLoader = loadHorseFrames): Riding {
  // Mount into the shared y-sorted entity layer (host) so the horse sorts with
  // trees/the hero instead of drawing behind all decor.
  const ownLayer = host ?? new Container();
  ownLayer.sortableChildren = true;

  let type: HorseType | null = null;
  let info: HorseTypeInfo | null = null;
  let sprite: Sprite | null = null;
  let hx = 0, hy = 0;           // horse world position
  let homeX = 0, homeY = 0;     // it grazes around here (updated on dismount)
  let target: { x: number; y: number } | null = null;
  let grazeMs = 0;
  let mounted = false;
  let flipped = false;
  let facing: HorseFacing = 'side';
  let animT = 0, frame = 0;
  let frames: HorseFrames | null = null;   // loaded once per horse type
  let loadToken = 0;                       // guards a stale load resolving after a re-tame

  const clearSprite = () => { sprite?.destroy(); sprite = null; };
  // Fall back to the side row (always present) so a missing up/down sequence can
  // never blank the horse mid-walk.
  const seqFor = (anim: HorseAnim): Texture[] | null =>
    frames?.[`${anim}_${facing}`] ?? frames?.[`${anim}_side`] ?? frames?.['idle_side'] ?? null;

  return {
    get mounted() { return mounted; },
    get type() { return type; },
    setHorse(t, i, tx, ty) {
      clearSprite();
      type = t; info = i;
      hx = (tx + 0.5) * TILE; hy = (ty + 0.9) * TILE;
      homeX = hx; homeY = hy; target = null; grazeMs = rand(GRAZE_PAUSE_MIN, GRAZE_PAUSE_MAX);
      facing = 'side'; flipped = false; frame = 0; animT = 0;
      frames = null;
      const token = ++loadToken;
      void loader(t).then((f) => {
        if (token !== loadToken) return;   // a different horse was set meanwhile
        frames = f;
        const first = f['idle_side']?.[0];
        if (sprite && first) sprite.texture = first;
      }).catch(() => { /* no frames → the horse stays invisible rather than a white box */ });
      const s = new Sprite(Texture.EMPTY);
      s.anchor.set(0.5, 0.9);
      s.scale.set(HORSE_PX_SCALE);
      s.x = hx; s.y = hy; s.zIndex = hy;
      ownLayer.addChild(s);
      sprite = s;
      mounted = false;
    },
    removeHorse() { clearSprite(); type = null; info = null; mounted = false; frames = null; loadToken++; },
    toggle(heroX, heroY) {
      if (!type) return 'no-horse';
      if (mounted) {
        // Dismount: the horse appears where the hero stands, and grazes from there.
        mounted = false;
        hx = heroX; hy = heroY; homeX = hx; homeY = hy; target = null;
        if (sprite) { sprite.visible = true; sprite.x = hx; sprite.y = hy; }
        return 'dismounted';
      }
      if (Math.hypot(heroX - hx, heroY - hy) / TILE > MOUNT_REACH_TILES) return 'too-far';
      mounted = true;
      if (sprite) sprite.visible = false;   // the ridden horse is drawn as part of the hero composite
      return 'mounted';
    },
    update(dt, heroX, heroY, blocked) {
      if (!sprite || !type) return;
      if (mounted) {
        // Ridden: the horse is part of the HERO's composite frame (player.ts RIDE
        // rows overlay Player_Horse_Brown.png UNDER the rider), so the loose sprite
        // stays hidden. Drawing it here too is what produced a second,
        // differently-scaled horse beneath a tiny rider.
        //
        // Its position is still tracked so a dismount puts the animal exactly where
        // the hero got off.
        hx = heroX; hy = heroY;
        sprite.visible = false;
        return;
      }
      sprite.visible = true;
      // The loose horse GRAZES around where it was left — a slow amble to a nearby
      // spot, then a pause. It does not follow the hero — you walk back to it.
      let moving = false;
      if (target) {
        const dx = target.x - hx, dy = target.y - hy, d = Math.hypot(dx, dy);
        if (d < 3) { target = null; grazeMs = rand(GRAZE_PAUSE_MIN, GRAZE_PAUSE_MAX); }
        else {
          const step = GRAZE_SPEED * TILE * dt;
          const nx = hx + (dx / d) * step, ny = hy + (dy / d) * step;
          if (!blocked || !blocked(nx, ny)) { hx = nx; hy = ny; moving = true; }
          else target = null;   // give up if blocked, pause and re-pick
          // Face where it is ACTUALLY going. Same convention as fauna.ts: the pack's
          // animal side art faces LEFT, so the sprite is flipped when moving RIGHT.
          // The old code flipped on dx < 0 — the horse moonwalked, which is the
          // "walking backwards" the player saw.
          if (Math.abs(dx) > Math.abs(dy)) { facing = 'side'; flipped = dx > 0; }
          else if (Math.abs(dy) > 0.01) { facing = dy < 0 ? 'up' : 'down'; flipped = false; }
        }
      } else {
        grazeMs -= dt * 1000;
        if (grazeMs <= 0) {
          const a = Math.random() * Math.PI * 2, r = rand(0.5, GRAZE_RADIUS) * TILE;
          target = { x: homeX + Math.cos(a) * r, y: homeY + Math.sin(a) * r };
        }
      }
      animT += dt;
      const fps = moving ? 7 : IDLE_FPS;
      if (animT >= 1 / fps) { animT = 0; frame++; }
      // A walking horse plays the WALK row (it used to cycle the 2 idle frames while
      // sliding across the grass, which is what read as a broken gait).
      const seq = seqFor(moving ? 'walk' : 'idle');
      if (seq?.length) sprite.texture = seq[frame % seq.length] ?? sprite.texture;
      sprite.x = hx; sprite.y = hy; sprite.zIndex = hy;
      sprite.scale.x = (flipped ? -1 : 1) * HORSE_PX_SCALE;
    },
    mountSpeed() { return mounted && info ? info.speed : 0; },
    fearless() { return mounted && !!info?.fearless; },
    position() { return type ? { tx: Math.floor(hx / TILE), ty: Math.floor(hy / TILE) } : null; },
    pixel() { return type ? { x: hx, y: hy } : null; },
    destroy() { sprite?.destroy(); sprite = null; if (!host) ownLayer.destroy({ children: true }); },
  };
}
